import React, { useState, useEffect, useRef } from 'react';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import { CalendarMonth as CalendarIcon } from '@mui/icons-material';
import { toISODateStr } from '../utils/dateUtils';

interface DateFieldProps {
  label: string;
  value: string; // YYYY-MM-DD
  onChange: (value: string) => void; // YYYY-MM-DD
  required?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  sx?: object;
  [key: string]: any; // allow extra props like key
}

function toDisplay(iso: string): string {
  if (!iso) return '';
  const parts = iso.split('-');
  if (parts.length !== 3) return iso;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

function fromDisplay(display: string): string {
  const clean = display.replace(/[^\d/]/g, '');
  const parts = clean.split('/');
  if (parts.length === 3 && parts[2].length === 4) {
    const [d, m, y] = parts;
    if (d && m && y) {
      const date = new Date(Number(y), Number(m) - 1, Number(d));
      if (!isNaN(date.getTime())) return toISODateStr(date);
    }
  }
  return '';
}

const DateField: React.FC<DateFieldProps> = ({
  label,
  value,
  onChange,
  required,
  size = 'small',
  fullWidth = true,
  disabled,
  sx,
  ...rest
}) => {
  const [display, setDisplay] = useState(toDisplay(value));
  const hiddenRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDisplay(toDisplay(value));
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value.replace(/[^\d/]/g, '');

    // Auto-insert slashes after day (2 digits) and month (2 digits)
    const digits = raw.replace(/\//g, '');
    if (digits.length <= 2) {
      raw = digits;
    } else if (digits.length <= 4) {
      raw = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    } else {
      raw = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
    }

    setDisplay(raw);

    const iso = fromDisplay(raw);
    if (iso) onChange(iso);
    else if (!raw) onChange('');
  };

  const handleBlur = () => {
    setDisplay(toDisplay(value));
  };

  const handlePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const iso = e.target.value; // YYYY-MM-DD
    if (iso) {
      setDisplay(toDisplay(iso));
      onChange(iso);
    }
  };

  const openPicker = () => {
    hiddenRef.current?.showPicker();
  };

  return (
    <>
      <TextField
        label={label}
        value={display}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="DD/MM/YYYY"
        required={required}
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
        sx={sx}
        InputLabelProps={{ shrink: true }}
        inputProps={{ maxLength: 10 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={openPicker} disabled={disabled} tabIndex={-1}>
                <CalendarIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        {...rest}
      />
      <input
        ref={hiddenRef}
        type="date"
        value={value || ''}
        onChange={handlePickerChange}
        style={{ position: 'fixed', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
      />
    </>
  );
};

export default DateField;
